import type { AssetSearchCriteria } from '@/api';
import { BaseSearchFilterDto } from '@/api';
import { queryToArray, queryToBoolean, queryToNumber } from '@/utils/query';
import i18n from '@/i18n';
import { computed } from 'vue';

export const INITIAL_SEARCH_PARAMS = {
  assetFilterType: [],
  datasource: [],
  healthStatus: [],
  usage: [],
  tag: [],
  term: [],
  owner: [],
  ingestion: [],
  customMetadataEntry: [],
  searchInFieldsAndDescriptions: false,
  textSearch: '',
  itemsPerPage: 50,
  page: 1,
  sort: [
    'relevance,ASC',
  ],
  domain: 'All',
};

export const EMPTY_ASSET_TYPE_FILTER = {
  type: BaseSearchFilterDto.type.ASSET_TYPE,
  query: 'asset_type',
  children: null,
};

export const EMPTY_TERM_FILTER = {
  type: BaseSearchFilterDto.type.TERM,
  query: 'term',
  children: null,
};

export const EMPTY_TAG_FILTER = {
  type: BaseSearchFilterDto.type.TAG,
  query: 'tag',
  children: null,
};

export const EMPTY_OWNER_FILTER = {
  type: BaseSearchFilterDto.type.OWNER,
  query: 'owner',
  children: null,
};

export const EMPTY_CUSTOM_METADATA_FILTER = {
  type: BaseSearchFilterDto.type.CUSTOM_METADATA,
  query: 'custom_metadata',
  children: null,
};

export const EMPTY_PLATFORM_FILTER = {
  type: BaseSearchFilterDto.type.PLATFORM,
  query: 'datasources',
  children: null,
};

export const EMPTY_HEALTH_STATUS_FILTER = {
  type: BaseSearchFilterDto.type.HEALTH_STATUS,
  query: 'health_status',
  children: null,
};

export const EMPTY_USAGE_FILTER = {
  type: BaseSearchFilterDto.type.USAGE,
  query: 'usage',
  children: [
    { results: 0, id: 'UNSUPPORTED' },
    { results: 0, id: 'LOW' },
    { results: 0, id: 'MEDIUM' },
    { results: 0, id: 'HIGH' },
  ],
};

export const EMPTY_ASSET_INGESTION_FILTER = {
  type: BaseSearchFilterDto.type.ASSET_INGESTION,
  query: 'asset_ingestion',
  children: [
    { results: 0, id: 'DECLARATIVE' },
    { results: 0, id: 'SIFFLET_SOURCED' },
  ],
};

export const SORT_CONFIGURATION = computed(() => [
  { value: 'name', text: i18n.t('common.words.name') },
  { value: 'relevance', text: i18n.t('data-catalog.relevance') },
]);

export const DEFAULT_OPTIONS = {
  itemsPerPage: 50,
  page: 1,
};

export const filtersValuesToQuery = (searchParams: AssetSearchCriteria): AssetSearchCriteria => {
  const {
    assetFilterType,
    datasource,
    healthStatus,
    usage,
    tag,
    term,
    owner,
    ingestion,
    textSearch,
    page,
    itemsPerPage,
    searchInFieldsAndDescriptions,
    sort,
    customMetadataEntry,
  } = searchParams;

  return {
    assetFilterType: queryToArray(assetFilterType!),
    datasource: queryToArray(datasource!),
    healthStatus: queryToArray(healthStatus!) as ('CRITICAL' | 'AT_RISK' | 'HEALTHY' | 'UNMONITORED' | 'NOT_SUPPORTED')[],
    usage: queryToArray(usage!) as ('UNSUPPORTED' | 'LOW' | 'MEDIUM' | 'HIGH')[],
    tag: queryToArray(tag!),
    term: queryToArray(term!),
    owner: queryToArray(owner!),
    customMetadataEntry: queryToArray(customMetadataEntry!),
    ingestion: queryToArray(ingestion!) as Array<'DECLARATIVE' | 'SIFFLET_SOURCED'>,
    textSearch: textSearch!,
    searchInFieldsAndDescriptions: queryToBoolean(searchInFieldsAndDescriptions!.toString()),
    sort: queryToArray(sort!),
    page: queryToNumber(page!.toString()),
    itemsPerPage: queryToNumber(itemsPerPage!.toString()),
  };
};
